// src/features/wbs/components/common/WbsFilterBar.tsx
import React from 'react';
import { STATUS_LABEL_KO, type WbsStatusKind } from './common/wbsStatus';

export type WeeklyFilterKey = 'NO_INPUT' | 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';

export type WbsFilters = {
    companyId: string; // '' = all
    statusKinds: string[]; // empty = all
    weeklyKinds: WeeklyFilterKey[]; // empty = all
    startSoon: boolean;
    finishSoon: boolean;

    // 확장(옵션)
    unassignedOnly: boolean;
    leafOnly: boolean;
    hasRiskOnly: boolean;
    hasTaskOnly: boolean;
};

export type WbsFilterOptions = {
    companies: Array<{ id: string; name: string }>;
    statusKinds: string[]; // 지연/정상/완료 등(동적 수집)
};

export function WbsFilterBar({
    filters,
    options,
    onChange,
    onResetFilters,
    showCompanyFilter = true,
    showUnassignedOnly = true,
    showRiskTaskFilters = true,
}: {
    filters: WbsFilters;
    options: WbsFilterOptions;
    onChange: (next: WbsFilters) => void;
    onResetFilters?: () => void;
    showCompanyFilter?: boolean;
    showUnassignedOnly?: boolean;
    showRiskTaskFilters?: boolean;
}) {
    const toggleArray = <T extends string>(arr: T[], v: T) =>
        arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

    const getStatusLabel = (k: string) => STATUS_LABEL_KO[k as WbsStatusKind] ?? k;

    const activeChips: string[] = [];
    if (showCompanyFilter && filters.companyId)
        activeChips.push(
            `회사: ${options.companies.find((c) => c.id === filters.companyId)?.name ?? filters.companyId}`,
        );

    if (showUnassignedOnly && filters.unassignedOnly) activeChips.push('미배정');
    // if (filters.statusKinds.length) activeChips.push(`상태: ${filters.statusKinds.join(',')}`);
    if (filters.statusKinds.length) activeChips.push(`상태: ${filters.statusKinds.map(getStatusLabel).join(',')}`)
    if (filters.weeklyKinds.length) activeChips.push(`주간: ${filters.weeklyKinds.join(',')}`);
    if (filters.startSoon) activeChips.push('착수임박(7일)');
    if (filters.finishSoon) activeChips.push('완료임박(7일)');
    if (filters.leafOnly) activeChips.push('WP(leaf)만');
    if (showRiskTaskFilters && filters.hasRiskOnly) activeChips.push('Risk 있음');
    if (showRiskTaskFilters && filters.hasTaskOnly) activeChips.push('Task 있음');

    return (
        <div className="mt-3 mb-3">
            <div className="rounded-lg border bg-white px-3 py-2">
                <div className="flex justify-between items-center">
                    <h1 className="text-sm font-medium pr-16">Filters</h1>
                    <div className="flex items-center gap-6 flex-wrap text-xs">
                        {/* 회사 */}
                        {showCompanyFilter || showUnassignedOnly ? (
                            <>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {showCompanyFilter && (
                                        <>
                                            <span className="text-gray-600">회사</span>
                                            <select
                                                className="text-xs border rounded-md px-2 py-1"
                                                value={filters.companyId}
                                                onChange={(e) => onChange({ ...filters, companyId: e.target.value })}
                                            >
                                                <option value="">전체</option>
                                                {options.companies.map((c) => (
                                                    <option key={c.id} value={c.id}>
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </>
                                    )}

                                    {showUnassignedOnly && (
                                        <label className="flex items-center gap-1">
                                            <input
                                                type="checkbox"
                                                checked={filters.unassignedOnly}
                                                onChange={() =>
                                                    onChange({ ...filters, unassignedOnly: !filters.unassignedOnly })
                                                }
                                            />
                                            미배정만
                                        </label>
                                    )}
                                </div>
                                <div className="h-4 w-px bg-gray-200" />
                            </>
                        ) : null}
                        {/* 상태(kind) */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-600">상태</span>
                            {options.statusKinds.map((k) => (
                                <label key={k} className="flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={filters.statusKinds.includes(k)}
                                        onChange={() =>
                                            onChange({ ...filters, statusKinds: toggleArray(filters.statusKinds, k) })
                                        }
                                    />
                                    {getStatusLabel(k)}
                                </label>
                            ))}
                        </div>
                        <div className="h-4 w-px bg-gray-200" />
                        {/* 주간 */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-600">주간</span>
                            {(
                                [
                                    ['NO_INPUT', '입력없음'],
                                    ['DRAFT', '입력됨(미제출)'],
                                    ['PENDING', '미승인'],
                                    ['APPROVED', '승인'],
                                    ['REJECTED', '반려'],
                                ] as const
                            ).map(([k, label]) => (
                                <label key={k} className="flex items-center gap-1">
                                    <input
                                        type="checkbox"
                                        checked={filters.weeklyKinds.includes(k)}
                                        onChange={() =>
                                            onChange({ ...filters, weeklyKinds: toggleArray(filters.weeklyKinds, k) })
                                        }
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                        <div className="h-4 w-px bg-gray-200" />
                        {/* 임박 */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <label className="flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    checked={filters.startSoon}
                                    onChange={() => onChange({ ...filters, startSoon: !filters.startSoon })}
                                />
                                착수임박(7일)
                            </label>
                            <label className="flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    checked={filters.finishSoon}
                                    onChange={() => onChange({ ...filters, finishSoon: !filters.finishSoon })}
                                />
                                완료임박(7일)
                            </label>
                        </div>
                        <div className="h-4 w-px bg-gray-200" />
                        {/* 확장 옵션 */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <label className="flex items-center gap-1">
                                <input
                                    type="checkbox"
                                    checked={filters.leafOnly}
                                    onChange={() => onChange({ ...filters, leafOnly: !filters.leafOnly })}
                                />
                                WP(leaf)만
                            </label>

                            {showRiskTaskFilters && (
                                <>
                                    <label className="flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            checked={filters.hasRiskOnly}
                                            onChange={() => onChange({ ...filters, hasRiskOnly: !filters.hasRiskOnly })}
                                        />
                                        Risk 있음
                                    </label>
                                    <label className="flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            checked={filters.hasTaskOnly}
                                            onChange={() => onChange({ ...filters, hasTaskOnly: !filters.hasTaskOnly })}
                                        />
                                        Task 있음
                                    </label>
                                </>
                            )}
                        </div>
                        <div className="h-4 w-px bg-gray-200" />
                        {/* 초기화 버튼 */}
                        {/* <div>
                            <button
                                type="button"
                                className="text-xs px-2 py-1 rounded-md border hover:bg-gray-50"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onChange({
                                        companyId: '',
                                        statusKinds: [],
                                        weeklyKinds: [],
                                        startSoon: false,
                                        finishSoon: false,
                                        unassignedOnly: false,
                                        leafOnly: false,
                                        hasRiskOnly: false,
                                        hasTaskOnly: false,
                                    });
                                }}
                            >
                                초기화
                            </button>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
